import React, { useCallback } from 'react'
import { createContext } from 'react'
import {
  BuiltinActionType,
  BuiltinEventType,
  KeyframeEventType,
  LiveConnectOptions,
  LiveEventType,
  LiveOptions,
  MicroStatus,
  RTCEventType,
  RTCType,
  UserInfo,
} from '../typings'
import { LiveState } from '../typings/live/LiveState'
import { createLive } from './createLive'
import { Live } from './live'

/**
 * LiveProvider 组件的属性类型
 * 
 * @template KeyframesSnapshot 帧数据快照类型
 */
export type LiveProviderPropTypes<KeyframesSnapshot extends Record<string, any>> = {
  /**
   * 子组件
   */
  children?: React.ReactNode
  /**
   * 自定义 Live 实例（可选，默认使用内部创建的实例）
   */
  live?: Live<KeyframesSnapshot>
}

/**
 * 创建 LiveReact 实例，并提供 React 技术栈相关工具链
 * 
 * 这个函数会创建一个 Live 实例和一系列 React Hooks，用于在 React 组件中方便地使用带看功能。
 * 返回的对象包含 LiveProvider 组件和各种 Hooks。
 * 
 * @template KeyframesSnapshot 帧数据快照类型，必须是一个对象类型
 * @param options 带看配置选项，详见 {@link LiveOptions}
 * @returns 包含 LiveProvider 组件和各种 Hooks 的对象
 * @example
 * ```typescript
 * // 定义帧数据类型
 * interface MyKeyframes {
 *   camera: { position: [number, number, number] }
 *   annotation: { text: string; position: [number, number] }
 * }
 * 
 * // 创建 LiveReact 实例
 * const { LiveProvider, useLiveAction, useKeyframe } = createLiveReact<MyKeyframes>({
 *   url: 'wss://example.com/live',
 *   getTicket: async () => 'ticket',
 *   getVoiceSign: async (params) => ({ sdkAppId: 0, userId: '', userSig: '', roomId: '' }),
 *   useBuiltInRTC: true
 * })
 * 
 * // 在组件中使用
 * function App() {
 *   return (
 *     <LiveProvider>
 *       <MyComponent />
 *     </LiveProvider>
 *   )
 * }
 * 
 * function MyComponent() {
 *   const { connect, sendKeyframe } = useLiveAction()
 *   const [camera, setCamera] = useKeyframe('camera')
 *   
 *   // 使用带看功能...
 * }
 * ```
 */
export function createLiveReact<KeyframesSnapshot extends Record<string, any>>(options: LiveOptions) {
  const LiveContext = createContext<Live<KeyframesSnapshot> | null>(null)
  const live = createLive<KeyframesSnapshot>(options)
  const jsBridge = live.jsBridge

  /**
   * LiveProvider 组件
   * 
   * 用于在 React 组件树中提供 Live 实例的上下文。
   * 所有子组件都可以通过 Hooks 访问 Live 实例。
   * 
   * @example
   * ```typescript
   * function App() {
   *   return (
   *     <LiveProvider>
   *       <MyComponent />
   *     </LiveProvider>
   *   )
   * }
   * ```
   */
  const LiveProvider = (props: LiveProviderPropTypes<KeyframesSnapshot>) => {
    return React.createElement(LiveContext.Provider, { value: live }, props.children)
  }

  /**
   * 获取当前 Live 实例的 Hook
   * 
   * 必须在 LiveProvider 的子组件中使用，否则会抛出错误。
   * 
   * @returns Live 实例
   * @throws {Error} 如果不在 LiveProvider 内部使用会抛出错误
   * @example
   * ```typescript
   * function MyComponent() {
   *   const live = useLiveInstance()
   *   
   *   useEffect(() => {
   *     live.on('stateChange', (state) => {
   *       console.log('状态变化:', state)
   *     })
   *   }, [live])
   * }
   * ```
   */
  const useLiveInstance = () => {
    const context = React.useContext(LiveContext)
    if (!context) throw new Error('LiveProvider never found.') // 如果没有找到LiveProvider，抛出错误
    return context
  }

  /**
   * 不安全地获取 Live 实例（不推荐使用）
   * 
   * 直接返回 Live 实例，不检查是否在 LiveProvider 内部。
   * 可能导致在未初始化时访问实例。
   * 
   * @deprecated 请使用 useLiveInstance 替代
   * @returns Live 实例
   */
  const unsafe__useLiveInstance = () => live

  /**
   * 获取当前 RTC 实例的 Hook
   * 
   * 返回 Live 实例使用的 RTC 实例，用于语音通信。
   * 
   * @returns RTC 协议实例
   * @example
   * ```typescript
   * function MyComponent() {
   *   const rtc = useRTCInstance()
   *   
   *   const handleToggleMicro = async () => {
   *     await rtc.toggleMicro(true)
   *   }
   * }
   * ```
   */
  const useRTCInstance = () => live.$RTC

  /**
   * 不安全地获取 RTC 实例（不推荐使用）
   * 
   * @deprecated 请使用 useRTCInstance 替代
   * @returns RTC 实例
   */
  const unsafe__useRTCInstance = () => useRTCInstance()

  /**
   * 带看连接操作的 Hook
   * 
   * 返回一个连接函数，用于连接到带看房间。
   * 
   * @returns 连接函数
   * @example
   * ```typescript
   * function MyComponent() {
   *   const connect = useConnect()
   *   
   *   const handleConnect = async () => {
   *     await connect({
   *       url: 'wss://example.com/live',
   *       getTicket: async () => 'ticket'
   *     })
   *   }
   * }
   * ```
   */
  const useConnect = () => async (options: LiveConnectOptions) => await live.connect(options)

  /**
   * 监听指定键的帧数据更新回调 Hook
   * 
   * 当指定键的帧数据发生变化时，会调用回调函数。
   * 回调函数会在组件卸载时自动清理。
   * 
   * @template KeyOfKeyframe 帧数据的键类型
   * @param key 帧数据的键
   * @param callback 回调函数，接收新的帧数据、旧的帧数据和请求ID作为参数
   * @param deps 依赖项数组，当依赖项变化时会重新注册回调
   * @returns 清理函数，可以手动取消监听
   * @example
   * ```typescript
   * function MyComponent() {
   *   useKeyframeUpdateCallback('camera', (nextFrame, prevFrame, requestId) => {
   *     console.log('相机位置更新:', nextFrame)
   *   }, [])
   * }
   * ```
   */
  function useKeyframeUpdateCallback<KeyOfKeyframe extends keyof KeyframeEventType<KeyframesSnapshot>>(
    key: KeyOfKeyframe,
    callback: (
      ...args: Parameters<KeyframeEventType<KeyframesSnapshot>[KeyOfKeyframe]>
    ) => ReturnType<KeyframeEventType<KeyframesSnapshot>[KeyOfKeyframe]>,
    deps: React.DependencyList = [],
  ) {
    const live = useLiveInstance()
    const dependencyList: React.DependencyList = [live, key, ...deps]
    const dispose = () => live.keyframes.off(key, callback)
    React.useEffect(() => {
      live.keyframes.on(key, callback)
      return dispose
    }, dependencyList)

    return dispose
  }

  /**
   * 带看操作函数的 Hook
   * 
   * 返回一组用于操作带看功能的函数，包括连接、发送帧数据、控制麦克风等。
   * 所有函数都使用 useCallback 包装，避免不必要的重新渲染。
   * 
   * @returns 包含各种带看操作函数的对象
   * @example
   * ```typescript
   * function MyComponent() {
   *   const {
   *     connect,
   *     sendKeyframe,
   *     toggleMicro,
   *     broadcast,
   *     exit,
   *     kick,
   *     forbidMicro,
   *     forbidSync,
   *     getFramesByKey,
   *     setSelfInfo,
   *     updateStatus
   *   } = useLiveAction()
   *   
   *   // 连接带看
   *   const handleConnect = async () => {
   *     await connect({ url: 'wss://example.com/live', getTicket: async () => 'ticket' })
   *   }
   *   
   *   // 发送帧数据
   *   const handleSendFrame = () => {
   *     sendKeyframe('camera', { position: [1, 2, 3] })
   *   }
   *   
   *   // 切换麦克风
   *   const handleToggleMicro = () => {
   *     toggleMicro(true)
   *   }
   * }
   * ```
   */
  function useLiveAction() {
    const live = useLiveInstance() // 获取当前的带看实例

    // 连接到带看
    const connect = useCallback(
      async (options: LiveConnectOptions) => {
        await live.connect(options)
      },
      [live],
    )

    // 切换麦克风状态
    const toggleMicro = useCallback(
      (microStatus: boolean) => {
        live.toggleMicro(microStatus)
      },
      [live],
    )

    // 广播消息
    const broadcast = useCallback(
      async (data: Record<string, any>, toUserIds: string | string[], timeout = 0) => {
        await live.broadcast(data, toUserIds, timeout)
      },
      [live],
    )

    // 退出带看
    const exit = useCallback(async () => {
      await live.exit()
    }, [live])

    // 踢出用户
    const kick = useCallback(
      async (toUserIds: string | string[]) => {
        await live.kick(toUserIds)
      },
      [live],
    )

    // 禁止麦克风
    const forbidMicro = useCallback(
      async (micro: MicroStatus, toUserIds: string | string[] = []) => {
        await live.forbidMicro(micro, toUserIds)
      },
      [live],
    )

    // 禁止同步
    const forbidSync = useCallback(
      (sync: boolean, toUserIds: string | string[] = []) => {
        live.forbidSync(sync, toUserIds)
      },
      [live],
    )

    // 根据关键帧获取帧数据
    const getFramesByKey = useCallback(
      (key: keyof KeyframesSnapshot) => {
        return live.getFramesByKey(key)
      },
      [live],
    )

    // 发送关键帧
    const sendKeyframe = useCallback(
      (key: keyof KeyframesSnapshot, frame: KeyframesSnapshot[keyof KeyframesSnapshot]) => {
        live.sendKeyframe(key, frame)
      },
      [live],
    )

    // 设置用户信息
    const setSelfInfo = useCallback(
      (userInfo: Partial<UserInfo>) => {
        live.setSelfInfo(userInfo)
      },
      [live],
    )

    // 更新状态
    const updateStatus = useCallback(
      (
        evtType: BuiltinEventType,
        atnType: BuiltinActionType,
        status: Record<string, any>,
        toUserIds?: string | string[],
      ) => {
        live.updateStatus(evtType, atnType, status, toUserIds)
      },
      [live],
    )

    return {
      connect,
      broadcast,
      exit,
      kick,
      toggleMicro,
      forbidMicro,
      forbidSync,
      sendKeyframe,
      getFramesByKey,
      setSelfInfo,
      updateStatus,
    }
  }

  /**
   * 使用指定键的帧数据 Hook
   * 
   * 返回指定键的帧数据状态和更新函数，类似于 useState。
   * 当帧数据发生变化时，状态会自动更新。
   * 
   * @template K 帧数据的键类型
   * @param key 帧数据的键
   * @returns 包含帧数据状态和更新函数的元组 [state, setState]
   * @example
   * ```typescript
   * function MyComponent() {
   *   const [camera, setCamera] = useKeyframe('camera')
   *   
   *   // 读取帧数据
   *   console.log('当前相机位置:', camera?.position)
   *   
   *   // 更新帧数据
   *   const handleUpdateCamera = () => {
   *     setCamera({ position: [1, 2, 3], rotation: [0, 0, 0] })
   *   }
   * }
   * ```
   */
  function useKeyframe<K extends keyof KeyframesSnapshot>(key: K) {
    const live = useLiveInstance()
    const [state, updateState] = React.useState<KeyframesSnapshot[K] | undefined>(live.getFramesByKey(key))

    React.useEffect(() => {
      const onUpdate = (nextKeyframes: KeyframesSnapshot[K]) => updateState(nextKeyframes)

      const evtName = key as unknown as keyof KeyframeEventType<KeyframesSnapshot>
      const callback = onUpdate as unknown as (
        ...args: Parameters<KeyframeEventType<KeyframesSnapshot>[keyof KeyframeEventType<KeyframesSnapshot>]>
      ) => ReturnType<KeyframeEventType<KeyframesSnapshot>[keyof KeyframeEventType<KeyframesSnapshot>]>
      live.keyframes.on(evtName, callback)
      return () => live.keyframes.off(evtName, callback)
    }, [live])

    const setState = React.useCallback((frame: KeyframesSnapshot[K]) => live.sendKeyframe(key, frame), [live])
    return [state, setState]
  }

  /**
   * 监听带看事件的 Hook
   * 
   * 监听指定的带看事件，当事件触发时调用回调函数。
   * 回调函数会在组件卸载时自动清理。
   * 
   * @template K 事件名称类型
   * @param name 事件名称
   * @param callback 回调函数，参数类型根据事件类型自动推断
   * @param deps 依赖项数组，当依赖项变化时会重新注册回调
   * @returns 清理函数，可以手动取消监听
   * @example
   * ```typescript
   * function MyComponent() {
   *   useLiveEventCallback('stateChange', (state, prevState) => {
   *     console.log(`状态从 ${prevState} 变为 ${state}`)
   *   }, [])
   *   
   *   useLiveEventCallback('userListUpdate', (userList, requestId) => {
   *     console.log('用户列表更新:', userList)
   *   }, [])
   * }
   * ```
   */
  function useLiveEventCallback<K extends keyof LiveEventType<KeyframesSnapshot>>(
    name: K,
    callback: (
      ...args: Parameters<LiveEventType<KeyframesSnapshot>[K]>
    ) => ReturnType<LiveEventType<KeyframesSnapshot>[K]>,
    deps: React.DependencyList = [],
  ) {
    const live = useLiveInstance()
    const dispose = () => live.off(name, callback)
    const dependencyList: React.DependencyList = [live, name, ...deps]

    React.useEffect(() => {
      live.on(name, callback)
      return dispose
    }, dependencyList)

    return dispose
  }

  /**
   * 获取带看连接状态的 Hook
   * 
   * 返回当前带看连接的状态，当状态发生变化时会自动更新。
   * 
   * @returns 当前连接状态
   * @example
   * ```typescript
   * function MyComponent() {
   *   const state = useLiveState()
   *   
   *   return (
   *     <div>
   *       连接状态: {state}
   *       {state === LiveState.OPEN && <div>已连接</div>}
   *       {state === LiveState.CONNECTING && <div>连接中...</div>}
   *     </div>
   *   )
   * }
   * ```
   */
  function useLiveState() {
    const live = useLiveInstance()
    const [state, updateState] = React.useState<LiveState>(live.state)
    React.useEffect(() => {
      const stateChange = (state: LiveState) => updateState(state)
      live.on('stateChange', stateChange)
      return () => live.off('stateChange', stateChange)
    }, [live])

    return state
  }

  /**
   * RTC 操作函数的 Hook
   * 
   * 返回一组用于操作 RTC 语音通信的函数。
   * 
   * @returns 包含各种 RTC 操作函数的对象
   * @example
   * ```typescript
   * function MyComponent() {
   *   const { join, quit, toggleMicro, detectMicro, shock } = useRTCAction()
   *   
   *   // 加入语音房间
   *   const handleJoin = async () => {
   *     await join({
   *       voiceId: 'voice-id',
   *       userId: 'user-id',
   *       roomId: 'room-id',
   *       type: RTCType.RealseeBrowserRTC
   *     })
   *   }
   *   
   *   // 检测麦克风权限
   *   const handleDetectMicro = async () => {
   *     const hasPermission = await detectMicro()
   *     console.log('麦克风权限:', hasPermission)
   *   }
   * }
   * ```
   */
  function useRTCAction() {
    const live = useLiveInstance()

    const join = (options: { voiceId: string; userId: string; roomId: string; type: RTCType }) =>
      live.$RTC.join(options)

    const quit = () => live.$RTC.quit()
    const shock = () => live.$RTC.shock?.()
    const toggleMicro = (flag?: boolean | undefined) => live.$RTC.toggleMicro(flag)

    const detectMicro = () => live.$RTC.detectMicro()

    return {
      join,
      quit,
      shock,
      toggleMicro,
      detectMicro,
    }
  }

  /**
   * 监听 RTC 事件的 Hook
   * 
   * 监听指定的 RTC 事件，当事件触发时调用回调函数。
   * 回调函数会在组件卸载时自动清理。
   * 
   * @template K 事件名称类型
   * @param name 事件名称
   * @param callback 回调函数，参数类型根据事件类型自动推断
   * @param deps 依赖项数组，当依赖项变化时会重新注册回调
   * @returns 清理函数，可以手动取消监听
   * @example
   * ```typescript
   * function MyComponent() {
   *   useRTCEventCallback('joined', (info) => {
   *     console.log('已加入语音房间:', info)
   *   }, [])
   *   
   *   useRTCEventCallback('userVolumes', (volumes) => {
   *     console.log('用户音量:', volumes)
   *   }, [])
   * }
   * ```
   */
  function useRTCEventCallback<K extends keyof RTCEventType>(
    name: K,
    callback: (...args: Parameters<RTCEventType[K]>) => ReturnType<RTCEventType[K]>,
    deps: React.DependencyList = [],
  ) {
    const live = useLiveInstance()
    const dispose = () => live.$RTC.off(name, callback)
    const dependencyList: React.DependencyList = [live, name, ...deps]
    React.useEffect(() => {
      live.$RTC.on(name, callback)
      return dispose
    }, dependencyList)

    return dispose
  }

  /**
   * 获取和设置当前用户信息的 Hook
   * 
   * 返回当前用户信息的状态和更新函数，类似于 useState。
   * 当用户信息发生变化时，状态会自动更新。
   * 
   * @returns 包含用户信息状态和更新函数的元组 [state, setState]
   * @example
   * ```typescript
   * function MyComponent() {
   *   const [selfInfo, setSelfInfo] = useSelfInfo()
   *   
   *   // 读取用户信息
   *   console.log('用户ID:', selfInfo?.id)
   *   console.log('麦克风状态:', selfInfo?.micro_status)
   *   
   *   // 更新用户信息
   *   const handleUpdateInfo = async () => {
   *     await setSelfInfo({
   *       extension: {
   *         nickname: '新昵称',
   *         avatar: 'https://example.com/avatar.jpg'
   *       }
   *     })
   *   }
   * }
   * ```
   */
  function useSelfInfo() {
    const live = useLiveInstance()
    const [state, updateState] = React.useState<Partial<UserInfo>>(live.selfInfo || {})

    const selfInfoUpdate = (userInfo: Partial<UserInfo>) => updateState(userInfo)

    React.useEffect(() => {
      live.on('selfInfoUpdate', selfInfoUpdate)
      return () => live.off('selfInfoUpdate', selfInfoUpdate)
    }, [live])

    const setState = React.useCallback(
      async (userInfo: Partial<UserInfo>) => {
        return await live.setSelfInfo(userInfo)
      },
      [live],
    )

    return [state, setState]
  }

  /**
   * 获取所有帧数据快照的 Hook
   * 
   * 返回当前所有帧数据的快照，当任何帧数据发生变化时会自动更新。
   * 
   * @returns 当前所有帧数据的快照
   * @example
   * ```typescript
   * function MyComponent() {
   *   const snapshot = useKeyframesSnapshot()
   *   
   *   return (
   *     <div>
   *       <div>相机位置: {snapshot.camera?.position?.join(', ')}</div>
   *       <div>标注: {snapshot.annotation?.text}</div>
   *     </div>
   *   )
   * }
   * ```
   */
  function useKeyframesSnapshot() {
    const live = useLiveInstance() // 获取当前的带看实例
    const [state, setState] = React.useState<Partial<KeyframesSnapshot>>(live.snapshot)

    React.useEffect(() => {
      const onKeyframes = (keyframes: Partial<KeyframesSnapshot>) => setState(keyframes) // 更新状态
      live.on('keyframes', onKeyframes) // 监听关键帧事件
      return () => live.off('keyframes', onKeyframes) // 清理监听
    }, [live]) // 添加依赖项

    return state // 返回当前状态
  }
  /**
   * 获取用户列表的 Hook
   * 
   * 返回当前房间内的用户列表，当用户列表发生变化时会自动更新。
   * 
   * @returns 用户信息数组
   * @example
   * ```typescript
   * function MyComponent() {
   *   const userList = useUserList()
   *   
   *   return (
   *     <div>
   *       <h3>房间用户 ({userList.length})</h3>
   *       {userList.map(user => (
   *         <div key={user.id}>
   *           {user.extension?.nickname || user.id}
   *           {user.micro_status ? '🎤' : '🔇'}
   *         </div>
   *       ))}
   *     </div>
   *   )
   * }
   * ```
   */
  function useUserList() {
    const live = useLiveInstance()
    const [state, updateState] = React.useState<UserInfo[]>(live.userList)

    const userListUpdate = (userList: UserInfo[]) => updateState(userList)

    React.useEffect(() => {
      live.on('userListUpdate', userListUpdate)
      return () => live.off('userListUpdate', userListUpdate)
    }, [live])

    return state
  }

  return {
    live,
    jsBridge,
    LiveProvider,
    unsafe__useLiveInstance,
    unsafe__useRTCInstance,
    useConnect,
    useKeyframe,
    useKeyframeUpdateCallback,
    useLiveAction,
    useLiveEventCallback,
    useLiveState,
    useRTCAction,
    useRTCEventCallback,
    useSelfInfo,
    useKeyframesSnapshot,
    useUserList,
  }
}
